import { FC, useContext, useState } from "react";
import {
  Alert,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
} from "@mui/material";
import { TestSubmitResponse, getTestResults } from "../../api/tests";
import { UserProgramContext } from "../../context/user-program/user-program";
import { assertNonNullable } from "../../utils/asserts";
import { ContainedButton } from "../styled/contained-button";
import { SectionStepEnabled } from "../../content/types";

type Props = {
  disabled: boolean;
  stepId: string;
};

export const SectionContentTest: FC<Props> = ({ stepId, disabled }) => {
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<string, string>
  >({});

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [testFailed, setTestFailed] = useState<boolean>(false);
  const [resultsLoading, setResultsLoading] = useState(false);

  const { program, dispatch } = useContext(UserProgramContext);

  assertNonNullable(program);

  const step = program.sections
    .map((section) => section.steps)
    .flat()
    .find((step) => step.enabled && step.id === stepId) as SectionStepEnabled;

  assertNonNullable(step);

  const testCompleted = !!step.completedOn;

  const handleAnswerChange = (questionId: string, value: string) => {
    setErrors((prev) => ({
      ...prev,
      [questionId]: "",
    }));

    setTestFailed(false);

    setSelectedAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const submitAnswers = () => {
    let hasErrors = false;
    step.questions.forEach((question) => {
      if (!selectedAnswers[question.id]) {
        hasErrors = true;
        setErrors((prev) => ({
          ...prev,
          [question.id]: "Bitte wählen Sie eine Antwort aus",
        }));
      }
    });

    if (hasErrors) {
      return;
    }

    setResultsLoading(true);

    getTestResults({ stepId: step.id, answers: selectedAnswers })
      .then((result: TestSubmitResponse) => {
        if (result.passed) {
          dispatch({
            type: "COMPLETE_TEST",
            payload: {
              stepId: step.id,
            },
          });
        } else {
          setTestFailed(true);
        }
      })
      .finally(() => setResultsLoading(false));
  };

  if (resultsLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Grid container direction="column">
      <Grid container item mt={3} justifyContent="space-between">
        {testFailed && (
          <Grid item xs={12}>
            <Alert severity="error">
              Sie haben eine oder mehrere Fragen nicht korrekt beantwortet.
              Bitte versuchen Sie es erneut.
            </Alert>
          </Grid>
        )}
        {testCompleted && (
          <Grid item xs={12}>
            <Alert severity="success">
              Sie haben diesen Test bestanden Das Video können Sie so oft
              schauen, wie Sie möchten.
            </Alert>
          </Grid>
        )}
      </Grid>
      {step.questions.map((question) => (
        <Grid key={question.statement} item mt={3}>
          <FormControl
            error={!!errors[question.id]}
            disabled={testCompleted || disabled}
          >
            <FormLabel id="demo-controlled-radio-buttons-group">
              {question.statement}
            </FormLabel>
            <RadioGroup
              aria-labelledby="demo-controlled-radio-buttons-group"
              name="controlled-radio-buttons-group"
              onChange={(event) =>
                handleAnswerChange(question.id, event.target.value)
              }
              value={selectedAnswers[question.id] || null}
            >
              {question.answers.map((answer) => (
                <FormControlLabel
                  key={answer.statement}
                  value={answer.id}
                  control={<Radio />}
                  label={answer.statement}
                />
              ))}
            </RadioGroup>
            {errors[question.id] && (
              <FormHelperText>{errors[question.id]}</FormHelperText>
            )}
          </FormControl>
        </Grid>
      ))}

      <Grid item mt={3} mb={2}>
        <ContainedButton
          disabled={testCompleted || disabled}
          onClick={submitAnswers}
        >
          Speichern
        </ContainedButton>
      </Grid>
    </Grid>
  );
};
